import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Package, Download, Upload, Loader2, CheckCircle2, Terminal, Wrench, FileBox, Sparkles, Map, Database } from "lucide-react";
import api, { safeCopy, formatApiErrorDetail, downloadAuthedFile } from "@/lib/api";
import { PROMPT_VAULT, PACK_BLUEPRINTS } from "@/content/creatorKit";

const TRELLIS_STEPS = [
  { title: "Get the model", body: "TRELLIS.2 is Microsoft's open image-to-3D model. Download TRELLIS.2-4B from HuggingFace (microsoft/TRELLIS.2-4B) — the same engine the studio Forge runs.", code: "huggingface-cli download microsoft/TRELLIS.2-4B" },
  { title: "Install the runtime", body: "Python 3.10+ with CUDA (8GB+ VRAM recommended). Clone the official TRELLIS.2 repo and install requirements — full guide linked in the pack.", code: "git clone https://github.com/microsoft/TRELLIS.2.git && cd TRELLIS.2 && pip install -r requirements.txt" },
  { title: "Generate locally", body: "Run your concept images through the local pipeline. No generation budgets, no queues — your GPU, your speed.", code: "python example_image_to_3d.py --image concept.png --output asset.glb" },
  { title: "Upload to the Forge", body: "Export GLB (max 60MB) and upload straight into your Forge job list below — then attach to packs exactly like cloud generations.", code: "" },
];

const RIG_CHECKLIST = [
  "Retopologize to game budgets (clothing 5-20k tris, props 1-10k tris)",
  "T-pose / A-pose for anything wearable",
  "Bone naming follows UE5 Mannequin convention where possible",
  "Mixamo auto-rig is acceptable for standard humanoids — note it in the pack description",
  "Textures packed inside the GLB (no external file dependencies)",
];

export function GlbUpload({ onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/creators/forge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded — it's in your 3D Models list`);
      onUploaded?.(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".glb" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <button data-testid="forge-upload-button" disabled={busy} onClick={() => inputRef.current?.click()} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-black transition-colors flex items-center gap-1.5">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload Local GLB
      </button>
    </>
  );
}

export default function LocalKitTab() {
  return (
    <div data-testid="local-kit-tab" className="flex flex-col gap-4">
      <div className="border border-nc-green/30 bg-nc-green/5 p-5 flex items-start gap-4">
        <Package className="w-6 h-6 text-nc-green shrink-0" />
        <div>
          <p className="font-sans font-bold text-sm text-zinc-100 uppercase">Your Local Forge Kit — owned for life</p>
          <p className="font-mono text-[11px] text-zinc-400 mt-1.5 leading-relaxed">
            Everything below is yours as part of activation. Run 3D generation on your own machine — no studio
            budgets, no queues. Cloud generation in the Forge tab stays available as a backup.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2"><Terminal className="w-3.5 h-3.5" /> Local TRELLIS setup</p>
          {TRELLIS_STEPS.map((s, i) => (
            <div key={i} data-testid={`kit-step-${i + 1}`} className="border border-white/10 bg-black/40 p-4">
              <p className="font-sans font-bold text-xs text-zinc-100 uppercase">{String(i + 1).padStart(2, "0")} // {s.title}</p>
              <p className="font-mono text-[11px] text-zinc-400 mt-1.5 leading-relaxed">{s.body}</p>
              {s.code && (
                <div className="mt-2 flex items-center justify-between gap-2 bg-[#050505] border border-white/10 px-3 py-2">
                  <code className="font-mono text-[10px] text-nc-green truncate">{s.code}</code>
                  <button onClick={async () => { (await safeCopy(s.code)) ? toast.success("Copied") : toast.error("Copy blocked"); }} className="font-mono text-[8px] uppercase text-zinc-500 hover:text-white shrink-0 transition-colors">copy</button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><Wrench className="w-3.5 h-3.5" /> Rig & export standards</p>
          <div className="border border-white/10 bg-black/40 p-4">
            {RIG_CHECKLIST.map((c, i) => (
              <p key={i} className="font-mono text-[11px] text-zinc-400 leading-relaxed flex items-start gap-2 mt-1.5 first:mt-0">
                <CheckCircle2 className="w-3.5 h-3.5 text-nc-green shrink-0 mt-0.5" /> {c}
              </p>
            ))}
          </div>
          <div className="border border-white/10 bg-black/40 p-4">
            <p className="font-sans font-bold text-xs text-zinc-100 uppercase flex items-center gap-2"><FileBox className="w-4 h-4 text-nc-blue" /> Official resources</p>
            {[
              ["TRELLIS.2 on HuggingFace", "https://huggingface.co/microsoft/TRELLIS.2-4B"],
              ["TRELLIS.2 GitHub repo", "https://github.com/microsoft/TRELLIS.2"],
              ["Mixamo auto-rigger", "https://www.mixamo.com"],
              ["Blender (free)", "https://www.blender.org/download/"],
            ].map(([label, url]) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-nc-blue hover:text-zinc-100 flex items-center gap-2 mt-2 transition-colors">
                <Download className="w-3 h-3" /> {label}
              </a>
            ))}
          </div>
          <div className="border border-nc-blue/30 bg-nc-blue/5 p-4">
            <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">
              <span className="text-nc-blue font-bold">SUBMISSION PATH:</span> generate locally → upload GLB via the Forge tab's
              <b> Upload Local GLB</b> button → attach to a pack → run the QA Pre-Check → submit. Local uploads get the same QA and the same bounty + royalty terms.
            </p>
          </div>
        </div>
      </div>

      {/* Starter Vault — studio reference GLBs */}
      <StarterVault />

      {/* Prompt Vault */}
      <div className="border border-white/10 bg-black/40 p-5">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2 mb-1"><Sparkles className="w-3.5 h-3.5" /> Prompt Vault</p>
        <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-relaxed">The studio's proven prompt patterns — copy one, adapt the subject, generate. One object, plain background, orthographic view: every time.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {PROMPT_VAULT.map((p, i) => (
            <div key={i} data-testid={`vault-prompt-${i}`} className="border border-white/10 bg-[#050505] p-3.5 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <p className="font-sans font-bold text-[11px] text-zinc-100 uppercase">{p.title}</p>
                <span className="font-mono text-[8px] text-nc-orange uppercase tracking-[0.1em] shrink-0">{p.category}</span>
              </div>
              <p className="font-mono text-[10px] text-zinc-500 leading-relaxed flex-1">{p.prompt}</p>
              <button onClick={async () => { (await safeCopy(p.prompt)) ? toast.success("Prompt copied — paste it in the Forge") : toast.error("Copy blocked"); }} className="self-start font-mono text-[8px] tracking-[0.15em] uppercase border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-2.5 py-1.5 transition-colors">
                Copy prompt
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Pack Blueprints */}
      <div className="border border-white/10 bg-black/40 p-5">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2 mb-1"><Map className="w-3.5 h-3.5" /> Pack Blueprints</p>
        <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-relaxed">Ready-to-build pack recipes — exact piece lists for packs that sell. Build all the pieces, assemble, submit.</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {PACK_BLUEPRINTS.map((b, i) => (
            <div key={i} data-testid={`blueprint-${i}`} className="border border-white/10 bg-[#050505] p-4 flex flex-col gap-2.5">
              <p className="font-sans font-bold text-xs text-zinc-100 uppercase">{b.title}</p>
              <p className="font-mono text-[9px] text-nc-blue uppercase tracking-[0.1em]">{b.category}</p>
              <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{b.concept}</p>
              <div className="flex flex-col gap-1">
                {b.pieces.map((piece) => (
                  <p key={piece} className="font-mono text-[10px] text-zinc-500 flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-nc-green shrink-0 mt-0.5" /> {piece}</p>
                ))}
              </div>
              <p className="font-mono text-[9px] text-zinc-600 leading-relaxed border-t border-white/5 pt-2 mt-auto">{b.tips}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StarterVault() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/creators/kit/starter").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <div data-testid="starter-vault" className="border border-white/10 bg-black/40 p-5">
      <p className="font-mono text-[9px] tracking-[0.25em] text-nc-green uppercase flex items-center gap-2 mb-1"><Database className="w-3.5 h-3.5" /> Starter Vault</p>
      <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-relaxed">Studio reference GLBs — download, open in Blender, and study the topology and texture standard your packs are QA'd against.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((it) => (
          <div key={it.id} className="border border-white/10 bg-[#050505] px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-sans font-semibold text-[11px] text-zinc-100 truncate">{it.label}</p>
              <p className="font-mono text-[8px] text-zinc-600">{(it.glb_size / 1048576).toFixed(1)} MB GLB</p>
            </div>
            <button data-testid={`starter-download-${it.id.slice(0, 8)}`} onClick={() => downloadAuthedFile(`/creators/kit/starter/${it.id}/glb`, `specpixel-starter-${it.id.slice(0, 8)}.glb`).catch((e) => toast.error(e.message))} className="font-mono text-[8px] tracking-[0.15em] uppercase border border-nc-green/60 text-nc-green hover:bg-nc-green/10 px-2.5 py-1.5 transition-colors shrink-0 flex items-center gap-1">
              <Download className="w-3 h-3" /> GLB
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
